import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Button, Table, message, Popconfirm, Space, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { GetSprintsByProject } from '../../../dao/abp/GetSprintsByProject';
import { PostSprint } from '../../../dao/abp/PostSprint';
import { DeleteSprint } from '../../../dao/abp/DeleteSprint';
import dayjs from 'dayjs';
import AvaluacioColors from '../../../styles/AvaluacioColors';

const { TextArea } = Input;
const { Title } = Typography;

interface Props {
    visible: boolean;
    onClose: () => void;
    idProjecte: string;
}

const SprintConfigModal: React.FC<Props> = ({ visible, onClose, idProjecte }) => {
    const [form] = Form.useForm();
    const [sprints, setSprints] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    useEffect(() => {
        if (visible) {
            loadSprints();
        }
    }, [visible]);

    const loadSprints = async () => {
        setLoading(true);
        const data = await GetSprintsByProject(idProjecte);
        setSprints(data || []);
        setLoading(false);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const sprint = {
                id: editingId || 0,
                numero: values.numero,
                titol: values.titol,
                descripcio: values.descripcio || '',
                data_inici: values.data_inici ? values.data_inici.format('YYYY-MM-DD HH:mm:ss') : null,
                data_final: values.data_final ? values.data_final.format('YYYY-MM-DD HH:mm:ss') : null,
            };

            const success = await PostSprint(idProjecte, sprint);

            if (success) {
                message.success(editingId ? 'Sprint actualizado' : 'Sprint creado');
                form.resetFields();
                setEditingId(null);
                loadSprints();
            } else {
                message.error('Error al guardar el sprint');
            }
            setLoading(false);
        } catch (error) {
            console.error('Validation Failed:', error);
            setLoading(false);
        }
    };

    const handleEdit = (record: any) => {
        setEditingId(record.id);
        form.setFieldsValue({
            numero: record.numero,
            titol: record.titol,
            descripcio: record.descripcio,
            data_inici: record.data_inici ? dayjs(record.data_inici) : null,
            data_final: record.data_final ? dayjs(record.data_final) : null,
        });
    };

    const handleDelete = async (id: number) => {
        setLoading(true);
        const success = await DeleteSprint(id.toString());
        if (success) {
            message.success('Sprint eliminado');
            loadSprints();
        } else {
            message.error('Error al eliminar el sprint');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        form.resetFields();
        setEditingId(null);
    };

    const columns = [
        {
            title: 'Número',
            dataIndex: 'numero',
            key: 'numero',
            width: 80,
        },
        {
            title: 'Título',
            dataIndex: 'titol',
            key: 'titol',
            width: 200,
        },
        {
            title: 'Fecha Inicio',
            dataIndex: 'data_inici',
            key: 'data_inici',
            width: 150,
            render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
        },
        {
            title: 'Fecha Fin',
            dataIndex: 'data_final',
            key: 'data_final',
            width: 150,
            render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
        },
        {
            title: 'Estado',
            dataIndex: 'estado_sprint',
            key: 'estado_sprint',
            width: 120,
            render: (estado: string) => {
                const estados: any = {
                    sin_fechas: { label: 'Sin fechas', color: 'default' },
                    no_iniciado: { label: 'No iniciado', color: 'blue' },
                    abierto: { label: 'Abierto', color: 'green' },
                    cerrado: { label: 'Cerrado', color: 'red' },
                };
                const e = estados[estado] || estados.sin_fechas;
                return <span style={{ color: e.color, fontWeight: 'bold' }}>{e.label}</span>;
            },
        },
        {
            title: 'Tareas',
            dataIndex: 'num_tasques',
            key: 'num_tasques',
            width: 80,
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="¿Eliminar este sprint?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Modal
            title={<Title level={4}>Gestión de Sprints</Title>}
            open={visible}
            onCancel={onClose}
            width={1000}
            footer={null}
        >
            <div style={{ marginBottom: 24 }}>
                <Title level={5}>{editingId ? 'Editar Sprint' : 'Nuevo Sprint'}</Title>
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="numero"
                        label="Número de Sprint"
                        rules={[{ required: true, message: 'Número requerido' }]}
                    >
                        <Input type="number" placeholder="Ej: 1" />
                    </Form.Item>

                    <Form.Item
                        name="titol"
                        label="Título"
                        rules={[{ required: true, message: 'Título requerido' }]}
                    >
                        <Input placeholder="Ej: Sprint 1 - Desarrollo básico" />
                    </Form.Item>

                    <Form.Item name="descripcio" label="Descripción">
                        <TextArea rows={2} placeholder="Descripción opcional del sprint" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item
                            name="data_inici"
                            label="Fecha de Inicio (Opcional)"
                            style={{ flex: 1 }}
                        >
                            <DatePicker
                                showTime
                                format="DD/MM/YYYY HH:mm"
                                placeholder="Seleccionar fecha"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="data_final"
                            label="Fecha de Fin (Opcional)"
                            style={{ flex: 1 }}
                        >
                            <DatePicker
                                showTime
                                format="DD/MM/YYYY HH:mm"
                                placeholder="Seleccionar fecha"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </div>

                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<PlusOutlined />}
                            loading={loading}
                            style={{ backgroundColor: AvaluacioColors.colors.color6 }}
                        >
                            {editingId ? 'Actualizar' : 'Crear'} Sprint
                        </Button>
                        {editingId && (
                            <Button onClick={handleCancel}>
                                Cancelar
                            </Button>
                        )}
                    </Space>
                </Form>
            </div>

            <div>
                <Title level={5}>Sprints Existentes</Title>
                <Table
                    columns={columns}
                    dataSource={sprints}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    size="small"
                />
            </div>
        </Modal>
    );
};

export default SprintConfigModal;
